import { Clipboard } from '@angular/cdk/clipboard';
import { Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { EDateFormat, EDateType, EShowTypes } from '@app/core/enums';
import { ElistDateFormat, EOperationType } from '@modules/factor/children/constructor/constains/constructor.enums';
import { DateStatusEnum } from '@modules/factor/children/scheduler/constains/scheduler.enums';
import { IRegistryReportingDate } from '@modules/factor/children/scheduler/interfaces/sheduler.interface';
import { EFactorFiledTypes } from '@modules/factor/constains/factor.enumes';
import { ENotifyTypes, NotifierService } from '@services/notifier.service';
import * as moment from 'moment';
import {
    InterfaceConfigData,
    InterfaceFactorDateFormat,
    InterfaceFactorDateType,
    InterfaceFactorFieldsTypes,
} from '../interfaces/factor-row.interface';

@Injectable({
    providedIn: 'root',
})
export class FactorRowService extends AppService {
    /** Массив файлов на удаление */
    removeFileUUids: string[] = [];

    /** Отображение условных полей после выбора условий */
    displayConditions = false;

    className = 'FactorRowService';

    /** Разрешить массовое редактирование для кнопки */
    isMassEditAllowed = false;

    /** URL страницы */
    url = '/factor/front_row';

    /** URL работы с файлами */
    fileUploadUrl: string = this.serverUrl + '/files/front_files/file_upload';

    /** ИД реестра */
    factorId: number = null;
    /** ИД данных реестра */
    factorDataId: string = null;

    /**
     * Получение значения поля даты в полном формате
     * @param fieldValue значение
     * @param field поле
     * @param type тип даты
     */
    getFullDateValue(fieldValue: any, field: any, type: string): moment.Moment {
        let result = null;
        if (fieldValue && fieldValue[type]) {
            let m = null;
            try {
                m = moment(fieldValue[type], 'DD.MM.YYYY');
            } catch (e) {}
            if (m && m.isValid()) {
                result = m;
            }
        } else if (field.is_current === '1') {
            result = moment().add(moment().utcOffset(), 'm').utc(false);
        } else if (field.is_current === '2' && field.default_value !== ',') {
            const v = moment(field.default_value, 'DD-MM-YYYY');
            if (v && v.isValid()) {
                result = v;
            }
        }
        return result ? result.format('YYYY-MM-DD') : null;
    }

    /**
     * Получение значения поля даты с временем в полном формате
     * @param fieldValue значение
     * @param field поле
     * @param type тип даты
     */
    getFullDateTimeValue(fieldValue: any, field: any, type: string): moment.Moment {
        let result = null;
        if (fieldValue && fieldValue[type]) {
            let m = null;
            try {
                m = moment(fieldValue[type], 'DD-MM-YYYY hh:mm');
            } catch (e) {}
            if (m && m.isValid()) {
                result = m;
            }
        } else if (field.is_current === '1') {
            result = moment().add(moment().utcOffset(), 'm').utc(false);
        } else if (field.is_current === '2' && field.default_value !== ',') {
            const v = moment(field.default_value, 'YYYY-MM-DD HH:mm');
            if (v && v.isValid()) {
                result = v;
            }
        }

        return result ? result.format('YYYY-MM-DD HH:mm') : null;
    }

    /**
     * Получение значения поля даты в сокращенном формате
     * @param fieldValue значение
     * @param field поле
     * @param typeM тип даты для месяца
     * @param typeY тип даты для года
     * @param date
     */
    getDateMYValue(fieldValue: any, field: any, typeM: string, typeY: string, date: string = null): moment.Moment {
        let result = null;
        const current = moment().add(moment().utcOffset(), 'm').utc(false);
        if (fieldValue && (fieldValue[typeM] || fieldValue[typeY])) {
            const m = moment([
                fieldValue[typeY] ? fieldValue[typeY] : current.year(),
                fieldValue[typeM] ? fieldValue[typeM] - 1 : current.month(),
                fieldValue[date] ? fieldValue[date] : current.date(),
            ]);

            m.add(moment().utcOffset(), 'm').utc(false);
            result = m;
        } else if (field.is_current === '1') {
            result = current.clone();
        } else if (field.is_current === '2' && field.default_value !== ',') {
            const v = moment(field.default_value, 'YYYY-MM');
            if (v && v.isValid()) {
                result = v;
            }
        }
        return result ?? null;
    }

    /**
     * Получение значения языкового поля
     * @param fieldValue значение
     * @param field поле
     * @param index индекс накопительного поля
     * @param key ключ данных
     */
    getLanguageValue(fieldValue: any, field: any, index: number, key: string): object {
        const value = {};

        if (!field.language || field.language?.length < 1) {
            return value;
        }

        if (index === null) {
            value[field.id] = {};
            field.language.forEach((lang) => {
                value[field.id][lang.language] = lang[key];
            });
        } else if (fieldValue) {
            field.language.forEach((lang) => {
                if (fieldValue.fid === lang?.property_id) {
                    if (!value[field.id]) {
                        value[field.id] = {};
                    }
                    value[field.id][lang.language] = lang[key];
                }
            });
        }
        return value;
    }

    /**
     * Получение типа даты выбранного параметра
     * @param field параметр
     * @param dateFormat Форматы дат
     */
    getDateType(field, dateFormat: InterfaceFactorDateFormat) {
        if (field && field.date_format && field.date_format === dateFormat.year) {
            return 'date-year';
        }
        if (field && field.date_format && field.date_format === dateFormat.monthYear) {
            return 'date-month';
        }
        if (field && field.date_format && field.date_format === dateFormat.dateTime) {
            return 'date-time';
        }
        if (field && field.date_format && field.date_format === dateFormat.month) {
            return 'month';
        }
        if (field && field.date_format && field.date_format === dateFormat.dayMonth) {
            return 'day-month';
        }
        return 'date';
    }

    /**
     * Изменение значения цифрового поля
     * @param fieldVal данные поля
     * @param val текущее значение из формы
     * @param calcRightFieldId Список id изменяемых полей
     * @param digitFields Список полей типа digit
     * @param notifier сервис всплывающих сообщений
     */
    changeDigitValue(fieldVal, val, calcRightFieldId: any, digitFields: any, notifier: NotifierService) {
        Object.keys(calcRightFieldId).forEach((keys) => {
            if (calcRightFieldId[keys]) {
                calcRightFieldId[keys].forEach((key) => {
                    if (key.id === fieldVal.id) {
                        const props = key.fieldProps;
                        if (
                            !props ||
                            !props.calc_right ||
                            props.calc_right.length === 0 ||
                            !props.calc_props ||
                            props.calc_props.length === 0
                        ) {
                            return;
                        }
                        const terms = props.calc_right.split(' ');
                        let expr = '';
                        //Props.calc_props
                        for (let i = 0; i < terms.length; i++) {
                            // Если это значение показателя
                            if (terms[i]) {
                                if (terms[i][0] === '{' && terms[i][terms[i].length - 1] === '}') {
                                    let fid = terms[i].substr(1, terms[i].length - 2);
                                    const fidDef = fid.split('|');
                                    let defaultValue = null;
                                    if (fidDef.length > 1) {
                                        fid = parseInt(fidDef[0]);
                                        defaultValue = fidDef[1] + '';
                                        defaultValue = defaultValue.replace(',', '.');
                                        defaultValue = parseFloat(defaultValue);
                                    } else {
                                        fid = parseInt(fid);
                                    }
                                    //++ получаем коэф. ед. изм.
                                    const ratio = props.calc_props[fid];
                                    let value;
                                    try {
                                        value = fid === key.id ? val : digitFields[fid].value;
                                    } catch (e) {
                                        value = 0;
                                    }
                                    let fieldValue = 0;
                                    if (value) {
                                        try {
                                            value = value + '';
                                            value = value.replace(',', '.');
                                            fieldValue = parseFloat(value) / ratio;
                                        } catch (e) {
                                            fieldValue = 0;
                                        }
                                        expr += fieldValue;
                                    } else if (defaultValue !== '') {
                                        expr += defaultValue;
                                    }
                                } else if (terms[i][0] === '[' && terms[i][terms[i].length - 1] === ']') {
                                    const operation = terms[i].substr(1, terms[i].length - 2);
                                    switch (operation) {
                                        case '+':
                                            expr += ' ' + operation + ' ';
                                            break;

                                        case '-':
                                            expr += ' ' + operation + ' ';
                                            break;

                                        case '*':
                                            expr += ' ' + operation + ' ';
                                            break;

                                        case '/':
                                            expr += ' ' + operation + ' ';
                                            break;
                                        case '%':
                                            expr = ' ( ' + expr + ' ) * 100.0 ';
                                            break;
                                    }
                                } else {
                                    expr += ' ' + terms[+i] + ' ';
                                }
                            }
                        }
                        try {
                            let result = eval(expr);
                            // Если попытка деления на 0, то зануляем
                            result = result === 'Infinity' ? null : result;

                            // Для целочисленных округляем до целого
                            if (result && props.number_type === 'int') {
                                result = result.toFixed(0);
                            }

                            // Для вещественных округляем с указанной точностью
                            if (result && props.number_type === 'float') {
                                result = result.toFixed(props.precision || 2);
                            }
                            key.parentField.setValue(result);
                        } catch (e) {
                            notifier.notify('error', this.language_dictionary.mistake_in_calculations);
                        }
                    }
                });
            }
        });
    }

    /**
     * Проверка отображения по родителям формы
     * @param parents список родителей
     * @param views условия отображения полей
     * @param fieldViewsValue Значения формы
     */
    isViewParents(parents: string[], views, fieldViewsValue: { [key: number]: any }) {
        let result = true;
        parents.forEach((id) => {
            if (views[id]) {
                result = this.getShowStatus(views, fieldViewsValue, id);
            }
        });
        return result;
    }

    /**
     * Получение статуса отображения элемента
     * @param views условия отображения полей
     * @param fieldViewsValue Значения формы
     * @param id идентификатор показателя
     */
    getShowStatus(views: any, fieldViewsValue: any, id: string): boolean {
        const result = this.checkEntry(views[id], fieldViewsValue);
        return result;
    }

    /**
     * Проставление валидации для поля фомы
     * @param form форма
     * @param id ид показателя
     * @param isView отображается ли поле
     * @param allFieldsForm Информация по полям формы
     * @param fieldsTypes Типы полей
     * @param views условия отображения полей
     * @param fieldViewsValue
     */
    setAllPropertiesValidation(
        form: FormGroup | any,
        id: string,
        isView: boolean,
        allFieldsForm: { [key: string]: any },
        fieldsTypes: InterfaceFactorFieldsTypes,
        views: { [key: string]: { [key: string]: object } },
        fieldViewsValue,
    ) {
        if (allFieldsForm[id]?.is_folder === '1' && Number(allFieldsForm[id]?.show_type) === EShowTypes.storyForm) {
            if (form?.get(id)?.controls) {
                Object.keys(form?.get(id)?.controls).forEach((key) => {
                    allFieldsForm[id].properties.forEach((property) => {
                        this.setAllPropertiesValidation(
                            form?.get(id)?.get(key),
                            property.id,
                            isView,
                            allFieldsForm,
                            fieldsTypes,
                            views,
                            fieldViewsValue,
                        );
                    });
                    form?.get(id)?.get(key)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                });
                form?.get(id)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
        } else if (
            allFieldsForm[id]?.is_folder === '1' &&
            Number(allFieldsForm[id]?.show_type) !== EShowTypes.storyForm
        ) {
            if (
                Number(allFieldsForm[id]?.show_type) === EShowTypes.alternativeForm &&
                allFieldsForm[id]?.is_required === '1'
            ) {
                this.setFieldValidate(form?.get(id), isView);
            }
            allFieldsForm[id].properties.forEach((property) => {
                if (!views[property.id]) {
                    this.setAllPropertiesValidation(
                        form,
                        property.id,
                        isView,
                        allFieldsForm,
                        fieldsTypes,
                        views,
                        fieldViewsValue,
                    );
                }
            });
        } else {
            if (allFieldsForm[id]?.is_required === '1') {
                if (isView && allFieldsForm[id]?.parents && allFieldsForm[id]?.parents?.length) {
                    isView = this.isViewParents(allFieldsForm[id]?.parents, views, fieldViewsValue);
                }
                if (
                    allFieldsForm[id]?.data_type === fieldsTypes.string ||
                    allFieldsForm[id]?.data_type === fieldsTypes.text
                ) {
                    this.setFieldValidate(form?.get(id)?.get(this.defaultLanguage), isView);
                    form?.get(id)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                } else if (allFieldsForm[id]?.data_type === fieldsTypes.link) {
                    this.setFieldValidate(form?.get(id)?.get('name')?.get(this.defaultLanguage), isView);
                    this.setFieldValidate(form?.get(id)?.get('url')?.get(this.defaultLanguage), isView);
                    form?.get(id)?.get('name')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                    form?.get(id)?.get('url')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                    form?.get(id)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                } else if (form?.get(id)?.controls && Object.keys(form?.get(id)?.controls).length) {
                    Object.keys(form?.get(id)?.controls).forEach((key) => {
                        this.setFieldValidate(form?.get(id)?.get(key), isView);
                    });
                    form?.get(id)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                } else {
                    this.setFieldValidate(form?.get(id), isView);
                }
            }
        }
    }

    /**
     * Проставление валидации полю
     * @param form форма
     * @param isView отображается ли поле
     */
    setFieldValidate(form: FormGroup, isView: boolean) {
        form?.setValidators(!isView ? [] : [Validators.required]);
        form?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }

    /**
     * Проверка отображения поля на форме
     * @param id ид поля
     * @param views условия НЕ отображения записей
     * @param fieldViewsValue Значения формы
     */
    isViewField(id: string, views: any, fieldViewsValue): boolean {
        if (!views[id]) {
            return true;
        }
        return this.getShowStatus(views, fieldViewsValue, id);
    }

    /**
     * Проверка на выполнение условия отображения
     * @param viewsConditions - условия отображения поля
     * @param fieldViewsValue значения формы
     */
    checkEntry(viewsConditions: any, fieldViewsValue: any) {
        let result = true; // Изначально предполагаем, что все условия выполнены
        let groupResult = true;
        let conditionResult = true;
        let cOperator: 'and' | 'or' = null;
        let gOperator: 'and' | 'or' = null;
        // Проходимся по группам
        for (const index in viewsConditions) {
            if (viewsConditions.hasOwnProperty(index)) {
                const group = viewsConditions[index];

                // Проходимся по условиям
                for (const indexC in group.conditions) {
                    if (group.conditions.hasOwnProperty(indexC)) {
                        const condition = group.conditions[indexC];
                        // Извлечение параметров из условия
                        const fieldId = condition.field_id;
                        const operation = condition.operation;
                        const cFieldType = condition.field_type;
                        // Значение поля
                        let fieldValue = fieldViewsValue[fieldId];
                        if (fieldValue === undefined) {
                            continue;
                        }
                        let fieldValueSecond = null;
                        let conditionValueMain = condition.value.main_value;
                        let conditionValueSecond = condition.value.second_value;
                        const fieldType = typeof fieldValue;
                        // Преобразование в нужные форматы
                        if (cFieldType === EFactorFiledTypes.date) {
                            switch (operation) {
                                case EOperationType.durationEqual:
                                case EOperationType.durationMore:
                                case EOperationType.durationLess:
                                    fieldValueSecond = new Date(fieldValue.to);
                                    fieldValue = new Date(fieldValue.from);
                                    break;
                                default:
                                    conditionValueMain = new Date(conditionValueMain);
                                    if (conditionValueSecond) {
                                        conditionValueSecond = new Date(conditionValueSecond);
                                    }
                                    // Обработка для дд.мм.гг
                                    if (condition.field_date_type === EDateType.single) {
                                        if (condition.field_date_format === EDateFormat.date) {
                                            conditionValueMain = conditionValueMain.setHours(0, 0, 0, 0);
                                            fieldValue = new Date(fieldValue).setHours(0, 0, 0, 0);
                                            conditionValueSecond =
                                                conditionValueSecond !== null
                                                    ? conditionValueSecond.setHours(0, 0, 0, 0)
                                                    : null;
                                        } else {
                                            fieldValue = new Date(fieldValue).getTime();
                                            conditionValueMain = conditionValueMain.getTime();
                                            conditionValueSecond = conditionValueSecond.getTime();
                                        }
                                    } else {
                                        if (condition.field_date_format === EDateFormat.date) {
                                            fieldValueSecond = new Date(fieldValue.to).setHours(0, 0, 0, 0);
                                            fieldValue = new Date(fieldValue.from).setHours(0, 0, 0, 0);
                                            if (conditionValueSecond) {
                                                conditionValueMain = conditionValueMain.setHours(0, 0, 0, 0);
                                                conditionValueSecond = conditionValueSecond.setHours(0, 0, 0, 0);
                                            }
                                        } else if (condition.field_date_format === EDateFormat.dateTime) {
                                            fieldValueSecond = new Date(fieldValue.to).getTime();
                                            fieldValue = new Date(fieldValue.from).getTime();
                                            conditionValueMain = conditionValueMain.getTime();
                                            conditionValueSecond = conditionValueSecond.getTime();
                                        }
                                    }
                                    break;
                            }
                        }
                        if (cFieldType === EFactorFiledTypes.digit) {
                            conditionValueMain = Number(conditionValueMain);
                            conditionValueSecond = Number(conditionValueSecond);
                            if (fieldValue !== '') {
                                fieldValue = Number(fieldValue);
                            }
                        }
                        if (condition.field_date_format !== null) {
                            condition.field_date_format = String(condition.field_date_format);
                        }
                        if (cFieldType === EFactorFiledTypes.list) {
                            if (fieldType === 'string') {
                                fieldValue = fieldValue.split(',');
                            }
                            if (fieldValue === null) {
                                fieldValue = [];
                            }
                        }
                        if (cFieldType === EFactorFiledTypes.bool) {
                            conditionValueMain = conditionValueMain === '1';
                        }

                        // Проверка в зависимости от типа условия
                        switch (operation) {
                            case EOperationType.filled:
                                // Заполнено
                                switch (fieldType) {
                                    case 'string':
                                    case 'number':
                                        conditionResult = fieldValue !== '' && fieldValue !== null;
                                        break;
                                    case 'object':
                                        conditionResult =
                                            JSON.stringify(fieldValue) !== '{}' &&
                                            fieldValue[this.defaultLanguage] !== '';
                                        break;
                                }
                                break;
                            case EOperationType.notFilled:
                                switch (fieldType) {
                                    case 'string':
                                    case 'number':
                                        conditionResult = fieldValue === '' || fieldValue === null;
                                        break;
                                    case 'object':
                                        conditionResult =
                                            fieldValue.length === 0 ||
                                            fieldValue[this.defaultLanguage] === '' ||
                                            fieldValue === '';
                                        break;
                                }
                                break;
                            case EOperationType.match:
                                // Соответствует
                                switch (cFieldType) {
                                    case EFactorFiledTypes.bool:
                                        conditionResult = conditionValueMain === fieldValue;
                                        break;
                                    case EFactorFiledTypes.list:
                                        conditionResult = AppHelper.arraysEqualUnordered(
                                            fieldValue,
                                            conditionValueMain,
                                        );
                                        break;
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                    case EFactorFiledTypes.numerator:
                                        conditionResult = conditionValueMain === fieldValue[this.defaultLanguage];
                                        break;
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_type) {
                                            case EDateType.single:
                                                switch (condition.field_date_format) {
                                                    case EDateFormat.date:
                                                        conditionResult = conditionValueMain === fieldValue;
                                                        break;
                                                    case EDateFormat.dateTime:
                                                        conditionResult = conditionValueMain === fieldValue;
                                                        break;
                                                    case EDateFormat.dayMonth:
                                                        const dateDM = new Date(conditionValueMain);
                                                        conditionResult =
                                                            dateDM.getDay() === fieldValue.dateFD &&
                                                            dateDM.getMonth() === fieldValue.dateFM;
                                                        break;
                                                    case EDateFormat.monthYear:
                                                        const dateMY = new Date(conditionValueMain);
                                                        conditionResult =
                                                            dateMY.getDay() === fieldValue.dateFY &&
                                                            dateMY.getMonth() === fieldValue.dateFM;
                                                        break;
                                                    case EDateFormat.year:
                                                        const dateY = new Date(conditionValueMain);
                                                        conditionResult = dateY.getFullYear() === fieldValue.dateFY;
                                                        break;
                                                    case EDateFormat.month:
                                                        const dateM = new Date(conditionValueMain);
                                                        conditionResult = dateM.getMonth() === fieldValue.dateFM;
                                                        break;
                                                }
                                                break;
                                            case EDateType.period:
                                                switch (condition.field_date_format) {
                                                    case EDateFormat.date:
                                                    case EDateFormat.dateTime:
                                                        conditionResult =
                                                            conditionValueMain === fieldValue &&
                                                            conditionValueSecond === fieldValueSecond;
                                                        break;
                                                    case EDateFormat.dayMonth:
                                                        const dateDM = new Date(conditionValueMain);
                                                        const dateTDM = new Date(conditionValueSecond);
                                                        conditionResult =
                                                            dateDM.getDay() === fieldValue.dateFD &&
                                                            dateDM.getMonth() === fieldValue.dateFM &&
                                                            dateTDM.getDay() === fieldValue.dateTD &&
                                                            dateTDM.getMonth() === fieldValue.dateTM;
                                                        break;
                                                    case EDateFormat.monthYear:
                                                        const dateMY = new Date(conditionValueMain);
                                                        const dateTMY = new Date(conditionValueSecond);
                                                        conditionResult =
                                                            dateMY.getFullYear() === fieldValue.dateFY &&
                                                            dateMY.getMonth() === fieldValue.dateFM &&
                                                            dateTMY.getFullYear() === fieldValue.dateFY &&
                                                            dateTMY.getMonth() === fieldValue.dateFM;
                                                        break;
                                                    case EDateFormat.year:
                                                        const dateY = new Date(conditionValueMain);
                                                        const dateTY = new Date(conditionValueSecond);
                                                        conditionResult =
                                                            dateY.getFullYear() === fieldValue.dateFY &&
                                                            dateTY.getFullYear() === fieldValue.dateTY;
                                                        break;
                                                    case EDateFormat.month:
                                                        const dateM = new Date(conditionValueMain);
                                                        const dateTM = new Date(condition.value.second_value);
                                                        conditionResult =
                                                            dateM.getMonth() === fieldValue.dateFM &&
                                                            dateTM.getMonth() === fieldValue.dateTM;
                                                        break;
                                                }
                                                break;
                                        }
                                        break;
                                    case EFactorFiledTypes.link:
                                        conditionResult = conditionValueMain === fieldValue.url;
                                        break;
                                    case EFactorFiledTypes.digit:
                                        conditionResult = conditionValueMain === fieldValue;
                                        break;
                                    case EFactorFiledTypes.address:
                                        break;
                                }
                                break;
                            case EOperationType.notMatch:
                                // Не соответствует
                                switch (cFieldType) {
                                    case EFactorFiledTypes.bool:
                                        conditionResult = conditionValueMain !== fieldValue;
                                        break;
                                    case EFactorFiledTypes.list:
                                        conditionResult = !AppHelper.arraysEqualUnordered(
                                            fieldValue,
                                            conditionValueMain,
                                        );
                                        break;
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                    case EFactorFiledTypes.numerator:
                                        conditionResult = conditionValueMain !== fieldValue[this.defaultLanguage];
                                        break;
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_type) {
                                            case EDateType.single:
                                                switch (condition.field_date_format) {
                                                    case EDateFormat.date:
                                                        conditionResult = conditionValueMain !== fieldValue;
                                                        break;
                                                    case EDateFormat.dateTime:
                                                        conditionResult = conditionValueMain !== fieldValue;
                                                        break;
                                                    case EDateFormat.dayMonth:
                                                        const dateDM = new Date(conditionValueMain);
                                                        conditionResult =
                                                            dateDM.getDay() !== fieldValue.dateFD &&
                                                            dateDM.getMonth() !== fieldValue.dateFM;
                                                        break;
                                                    case EDateFormat.monthYear:
                                                        const dateMY = new Date(conditionValueMain);
                                                        conditionResult =
                                                            dateMY.getDay() !== fieldValue.dateFY &&
                                                            dateMY.getMonth() !== fieldValue.dateFM;
                                                        break;
                                                    case EDateFormat.year:
                                                        const dateY = new Date(conditionValueMain);
                                                        conditionResult = dateY.getFullYear() !== fieldValue.dateFY;
                                                        break;
                                                    case EDateFormat.month:
                                                        const dateM = new Date(conditionValueMain);
                                                        conditionResult = dateM.getMonth() !== fieldValue.dateFM;
                                                        break;
                                                }
                                                break;
                                            case EDateType.period:
                                                switch (condition.field_date_format) {
                                                    case EDateFormat.date:
                                                    case EDateFormat.dateTime:
                                                        conditionResult =
                                                            conditionValueMain !== fieldValue ||
                                                            conditionValueSecond !== fieldValueSecond;
                                                        break;
                                                    case EDateFormat.dayMonth:
                                                        const dateDM = new Date(conditionValueMain);
                                                        const dateTDM = new Date(conditionValueSecond);
                                                        conditionResult =
                                                            dateDM.getDay() !== fieldValue.dateFD &&
                                                            dateDM.getMonth() !== fieldValue.dateFM &&
                                                            dateTDM.getDay() !== fieldValue.dateTD &&
                                                            dateTDM.getMonth() !== fieldValue.dateTM;
                                                        break;
                                                    case EDateFormat.monthYear:
                                                        const dateMY = new Date(conditionValueMain);
                                                        const dateTMY = new Date(conditionValueSecond);
                                                        conditionResult =
                                                            dateMY.getFullYear() !== fieldValue.dateFY &&
                                                            dateMY.getMonth() !== fieldValue.dateFM &&
                                                            dateTMY.getFullYear() !== fieldValue.dateFY &&
                                                            dateTMY.getMonth() !== fieldValue.dateFM;
                                                        break;
                                                    case EDateFormat.year:
                                                        const dateY = new Date(conditionValueMain);
                                                        const dateTY = new Date(conditionValueSecond);
                                                        conditionResult =
                                                            dateY.getFullYear() !== fieldValue.dateFY &&
                                                            dateTY.getFullYear() !== fieldValue.dateTY;
                                                        break;
                                                    case EDateFormat.month:
                                                        const dateM = new Date(conditionValueMain);
                                                        const dateTM = new Date(conditionValueMain);
                                                        conditionResult =
                                                            dateM.getMonth() !== fieldValue.dateFM &&
                                                            dateTM.getMonth() !== fieldValue.dateTM;
                                                        break;
                                                }
                                                break;
                                        }
                                        break;
                                    case EFactorFiledTypes.link:
                                        conditionResult = conditionValueMain !== fieldValue.url;
                                        break;
                                    case EFactorFiledTypes.digit:
                                        conditionResult = conditionValueMain !== fieldValue;
                                        break;
                                    case EFactorFiledTypes.address:
                                        break;
                                }
                                break;
                            case EOperationType.Include:
                                // Содержит хотя бы одно из значений
                                switch (cFieldType) {
                                    case EFactorFiledTypes.list:
                                        conditionResult = AppHelper.hasCommonElement(fieldValue, conditionValueMain);
                                        break;
                                }
                                break;
                            case EOperationType.notInclude:
                                // Не содержит ни одного из значений
                                switch (cFieldType) {
                                    case EFactorFiledTypes.list:
                                        conditionResult = !AppHelper.hasCommonElement(fieldValue, conditionValueMain);
                                        break;
                                }
                                break;
                            case EOperationType.contains:
                                // Содержит
                                switch (cFieldType) {
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                    case EFactorFiledTypes.numerator:
                                        conditionResult = fieldValue[this.defaultLanguage]
                                            .toLowerCase()
                                            .includes(conditionValueMain.toLowerCase());
                                        break;
                                    case EFactorFiledTypes.address:
                                        break;
                                }
                                break;
                            case EOperationType.notContains:
                                // Не содержит
                                switch (cFieldType) {
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                    case EFactorFiledTypes.numerator:
                                        conditionResult = !fieldValue[this.defaultLanguage]
                                            .toLowerCase()
                                            .includes(conditionValueMain.toLowerCase());
                                        break;
                                    case EFactorFiledTypes.address:
                                        break;
                                }
                                break;
                            case EOperationType.more:
                                // Больше
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = Number(fieldValue) > conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.moreOrEqual:
                                // Больше или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = Number(fieldValue) >= conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.less:
                                // Меньше
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = Number(fieldValue) < conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.lessOrEqual:
                                // Меньше или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = Number(fieldValue) <= conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.later:
                                // Позже
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult = fieldValue > conditionValueMain;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                conditionResult = fieldValue.dateFY < conditionValueMain.getFullYear();
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.laterEqual:
                                // Позже или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult = fieldValue >= conditionValueMain;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.earlier:
                                // Раньше
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult = fieldValue < conditionValueMain;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.earlierEqual:
                                // Раньше или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult = fieldValue <= conditionValueMain;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.inInterval:
                                // Попадает в интервал
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        if (condition.field_date_type === EDateType.single) {
                                            switch (condition.field_date_format) {
                                                case EDateFormat.date:
                                                case EDateFormat.dateTime:
                                                    conditionResult =
                                                        fieldValue >= conditionValueMain &&
                                                        fieldValue <= conditionValueSecond;
                                                    break;
                                                case EDateFormat.dayMonth:
                                                    break;
                                                case EDateFormat.monthYear:
                                                    break;
                                                case EDateFormat.year:
                                                    break;
                                                case EDateFormat.month:
                                                    break;
                                            }
                                        } else if (condition.field_date_type === EDateType.period) {
                                            switch (condition.field_date_format) {
                                                case EDateFormat.date:
                                                case EDateFormat.dateTime:
                                                    conditionResult =
                                                        fieldValue >= conditionValueMain &&
                                                        fieldValueSecond <= conditionValueSecond;
                                                    break;
                                                case EDateFormat.dayMonth:
                                                    break;
                                                case EDateFormat.monthYear:
                                                    break;
                                                case EDateFormat.year:
                                                    break;
                                                case EDateFormat.month:
                                                    break;
                                            }
                                        }
                                        break;
                                    case EFactorFiledTypes.digit:
                                        conditionResult =
                                            fieldValue >= conditionValueMain && fieldValue <= conditionValueSecond;
                                }
                                break;
                            case EOperationType.foundOne:
                                // Есть хотя бы один
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                        conditionResult = Object.keys(fieldValue).length > 0;
                                        break;
                                    case EFactorFiledTypes.gallery:
                                        conditionResult = fieldValue.length > 0;
                                        break;
                                    case EFactorFiledTypes.relation:
                                        conditionResult = fieldValue !== null;
                                        break;
                                }
                                break;
                            case EOperationType.notFoundOne:
                                // Нет ни одного
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                        conditionResult = Object.keys(fieldValue).length === 0;
                                        break;
                                    case EFactorFiledTypes.gallery:
                                        conditionResult = fieldValue.length === 0;
                                        break;
                                    case EFactorFiledTypes.relation:
                                        conditionResult = fieldValue === null;
                                        break;
                                }
                                break;
                            case EOperationType.fileSigned:
                                // Все файлы подписаны
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                    case EFactorFiledTypes.gallery:
                                        for (const key in fieldValue) {
                                            if (fieldValue.hasOwnProperty(key)) {
                                                const value = fieldValue[key];
                                                conditionResult = value.signs.length > 0;
                                                if (!conditionResult) {
                                                    break;
                                                }
                                            }
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.nameFileInclude:
                                // В названии или имени содержится
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                    case EFactorFiledTypes.gallery:
                                        conditionResult = false;
                                        for (const key in fieldValue) {
                                            if (fieldValue.hasOwnProperty(key)) {
                                                const value = fieldValue[key];
                                                conditionResult =
                                                    value._name
                                                        .toLowerCase()
                                                        .includes(conditionValueMain.toLowerCase()) || conditionResult;
                                            }
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.durationEqual:
                                // Продолжительность равна
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                const duration = this.#getDuration(fieldValue, fieldValueSecond);

                                                switch (conditionValueSecond) {
                                                    case ElistDateFormat.day:
                                                        conditionResult = duration.days === conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.month:
                                                        conditionResult = duration.months === conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.year:
                                                        conditionResult = duration.years === conditionValueMain;
                                                        break;
                                                }
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.durationMore:
                                // Продолжительность более
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                const duration = this.#getDuration(fieldValue, fieldValueSecond);

                                                switch (conditionValueSecond) {
                                                    case ElistDateFormat.day:
                                                        conditionResult = duration.days > conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.month:
                                                        conditionResult = duration.months > conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.year:
                                                        conditionResult = duration.years > conditionValueMain;
                                                        break;
                                                }
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.durationLess:
                                // Продолжительность менее
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                const duration = this.#getDuration(fieldValue, fieldValueSecond);

                                                switch (conditionValueSecond) {
                                                    case ElistDateFormat.day:
                                                        conditionResult = duration.days < conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.month:
                                                        conditionResult = duration.months < conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.year:
                                                        conditionResult = duration.years < conditionValueMain;
                                                        break;
                                                }
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.beginsInInterval:
                                // Начинается в интервале
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult =
                                                    fieldValue >= conditionValueMain &&
                                                    fieldValue <= conditionValueSecond;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.endInInterval:
                                // Заканчивается в интервале
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult =
                                                    fieldValueSecond >= conditionValueMain &&
                                                    fieldValueSecond <= conditionValueSecond;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            default:
                                conditionResult = false; // Неподдерживаемый тип условия
                                break;
                        }

                        if (cOperator === 'and') {
                            groupResult = groupResult && conditionResult; // Объединение с "И"
                        } else if (cOperator === 'or') {
                            groupResult = groupResult || conditionResult; // Объединение с "ИЛИ"
                        } else {
                            groupResult = conditionResult; // Если оператора нет
                        }

                        // Если результат уже ложный и оператор "И", выходим из цикла условий
                        if ((!groupResult && cOperator === 'and') || condition.operator === null) {
                            break;
                        }
                        cOperator = condition.operator;
                    }
                }

                if (gOperator === 'and') {
                    result = result && groupResult; // Объединение с "И"
                } else if (gOperator === 'or') {
                    result = result || groupResult; // Объединение с "ИЛИ"
                } else {
                    result = groupResult; // Если оператора нет
                }

                // Если результат уже ложный и оператор "И", выходим из цикла групп
                if ((!result && gOperator === 'and') || group.operator === null) {
                    break;
                }
                gOperator = group.operator;
            }
        }
        return result; // Вернуть итоговый результат проверки условий
    }

    /**
     * Функция для получения разницы в днях, месяцах и годах
     * @param startDate
     * @param endDate
     */
    #getDuration(
        startDate: Date,
        endDate: Date,
    ): {
        days: number;
        months: number;
        years: number;
    } {
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffMonths =
            (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth();
        const diffYears = endDate.getFullYear() - startDate.getFullYear();

        return { days: diffDays, months: diffMonths, years: diffYears };
    }

    /**
     * Преобразование строки в дату
     * @param value значение
     */
    convertDateString(value) {
        let valueDate = null;
        if (typeof value === 'string') {
            try {
                if (value.length === 16) {
                    valueDate = moment(value, 'YYYY-MM-DD HH:mm').add(moment().utcOffset(), 'm').utc(false);
                } else if (value.length <= 2) {
                    valueDate = value;
                } else {
                    valueDate = moment(value, 'YYYY-MM-DD').add(12, 'hours').add(moment().utcOffset(), 'm').utc(false);
                }
            } catch (e) {}
        } else {
            valueDate = value;
        }
        return valueDate;
    }

    /**
     * Получение данных по полю даты
     * @param field данные поля
     * @param value значение
     * @param dateFormat Форматы дат
     * @param dateType Типы дат
     */
    getValueDate(field: any, value: any, dateFormat: InterfaceFactorDateFormat, dateType: InterfaceFactorDateType) {
        const result = {
            value: null,
            valueTo: null,
            valueFM: null,
            valueTM: null,
            valueFY: null,
            valueTY: null,
            valueFF: null,
            valueFT: null,
            valueFD: null,
            valueTD: null,
        };
        let from = null;
        let to = null;
        let valueDate = null;
        if (field.date_type === dateType.period) {
            from = value?.from ? this.convertDateString(value?.from) : null;
            to = value?.to ? this.convertDateString(value?.to) : null;
        } else {
            valueDate = value ? this.convertDateString(value) : null;
        }
        if (value && field.date_format && field.date_format === dateFormat.date) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.value = from.format('DD.MM.YYYY');
                }
                if (to) {
                    result.valueTo = to.format('DD.MM.YYYY');
                }
            } else {
                result.value = valueDate ? valueDate.format('DD.MM.YYYY') : null;
            }
        } else if (value && field.date_format && field.date_format === dateFormat.dateTime) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFF = from.format('DD.MM.YYYY HH:mm');
                }
                if (to) {
                    result.valueFT = to.format('DD.MM.YYYY HH:mm');
                }
            } else {
                result.valueFF = valueDate ? valueDate.format('DD.MM.YYYY HH:mm') : null;
            }
        } else if (value && field.date_format && field.date_format === dateFormat.monthYear) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFM = from.format('MM');
                    result.valueFY = from.format('YYYY');
                }
                if (to) {
                    result.valueTM = to.format('MM');
                    result.valueTY = to.format('YYYY');
                }
            } else {
                result.valueFM = valueDate ? valueDate.format('MM') : null;
                result.valueFY = valueDate ? valueDate.format('YYYY') : null;
            }
        } else if (value && field.date_format && field.date_format === dateFormat.month) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFM = from;
                }
                if (to) {
                    result.valueTM = to;
                }
            } else {
                result.valueFM = valueDate ? valueDate : null;
            }
        } else if (value && field.date_format && field.date_format === dateFormat.year) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFY = from.format('YYYY');
                }
                if (to) {
                    result.valueTY = to.format('YYYY');
                }
            } else {
                result.valueFY = +valueDate ? valueDate?.format('YYYY') : null;
            }
        } else if (value && field.date_format === dateFormat.dayMonth) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFD = from.format('DD');
                    result.valueFM = from.format('MM');
                }
                if (to) {
                    result.valueTD = to.format('DD');
                    result.valueTM = to.format('MM');
                }
            } else {
                result.valueFD = valueDate?.format('DD') ?? null;
                result.valueFM = valueDate?.format('MM') ?? null;
            }
        }
        return result;
    }

    /**
     * Добавление языковых данных
     * @param languageData языковые данные
     * @param order порядок значений
     * @param parentId ид родительской записи
     * @param id ИД характеристики
     * @param data данные
     * @param keyField ключ поля
     */
    addLangData(languageData: any, order: number, parentId: string, id: string, data: any, keyField: string) {
        Object.keys(data).forEach((k) => {
            if (!languageData[id]) {
                languageData[id] = [];
            }
            const valueLang = {
                factor_id: id,
                language: k,
                pid: parentId,
                order,
            };
            valueLang[keyField] = data[k];
            languageData[id].push(valueLang);
        });
    }

    /**
     * Добавление языковых данных для поля типа УРЛ
     * @param languageData языковые данные
     * @param order порядок значений
     * @param parentId ид родительской записи
     * @param id ИД характеристики
     * @param data данные
     */
    public addLangLinkFieldData(languageData: any, order: number, parentId: string, id: string, data: any) {
        Object.keys(data?.url).forEach((k) => {
            if (!languageData[id]) {
                languageData[id] = [];
            }
            const valueLang = {
                factor_id: id,
                language: k,
                pid: parentId,
                order,
                link_url: null,
                link_name: null,
            };
            valueLang.link_url = data?.url[k];
            valueLang.link_name = data?.name[k];
            languageData[id].push(valueLang);
        });
    }

    /**
     * Проверка запрета на загрузку файлов (по источнику заявки)
     */
    isDownloadDenied(form: FormGroup, applicationSourceId: number, applicationSourceData: InterfaceConfigData) {
        const applicationSource = form.get('values').get(String(applicationSourceId)).value;
        return applicationSource === applicationSourceData.epgu || applicationSource === applicationSourceData.ais_mfc
            ? 0
            : 1;
    }

    /**
     * Сформировать ссылку на карточку и поместить ее в буфер обмена
     * @param clipboard - Буфер обмена
     * @param text - HTMLElement карточки
     * @param notifier - сервис уведомлений
     * @param type - только ссылка или ссылка + заголовок
     */
    public copyTitle(
        clipboard: Clipboard,
        text: HTMLElement,
        notifier: NotifierService,
        type: 'link' | 'titleAndLink' = 'link',
    ): void {
        let copyElement: string;
        let message: string;

        switch (type) {
            case 'link':
                copyElement = window.location.href;
                message = this.language_dictionary.row_title_copy_success;
                break;
            case 'titleAndLink':
                copyElement = (text?.innerText ? text?.innerText : '') + '\x20\n' + window.location.href;
                message = this.language_dictionary.row_title_and_link_copy_success;
                break;
        }

        clipboard.copy(copyElement);
        notifier.notify(ENotifyTypes.success, message);
    }

    /**
     * Установка данных по датам в форму
     * @param form - форма
     * @param formData - данные записи
     */
    public setShedulerDates(
        form: FormGroup,
        formData: { [key: string]: any; scheduler_dates?: IRegistryReportingDate[] },
    ): void {
        let dayBefore = null;
        let dayAfter = null;
        let nextReportingDate = null;
        let currentDoc = null;
        let index = -1;

        if (formData.scheduler_date) {
            currentDoc = moment(formData.scheduler_date, 'YYYY-MM-DD');
        }

        if (formData?.scheduler_dates && formData?.scheduler_dates?.length > 0) {
            if (formData.scheduler_date) {
                const date = moment(formData.scheduler_date, 'YYYYMMDD').format('YYYY-MM-DD');
                index = formData?.scheduler_dates.findIndex((row) => row?.date === date);
            } else {
                index = formData?.scheduler_dates.findIndex((row) => row?.reporting_date_status === 'current');
            }
        }

        if (currentDoc === null) {
            return;
        }

        form.get('reporting_date').setValue(currentDoc, { eventEmit: false });

        if (formData?.period_input_before && formData?.period_input_before > 0) {
            dayBefore = moment(currentDoc)?.add(-1 * Number(formData?.period_input_before), 'days');
            form.get('reporting_date_earlier').setValue(dayBefore, { eventEmit: false });
        }

        if (formData?.period_input_after && formData?.period_input_after > 0) {
            dayAfter = moment(currentDoc)?.add(Number(formData?.period_input_after), 'days');
            form.get('reporting_date_later').setValue(dayAfter, { eventEmit: false });
        }

        if (index > -1) {
            if (formData?.scheduler_dates[index + 1]) {
                nextReportingDate = moment(formData?.scheduler_dates[index + 1].date);
                form.get('reporting_date_next').setValue(nextReportingDate, { eventEmit: false });
            }

            if (formData?.scheduler_dates[index]?.reporting_date_status === DateStatusEnum.Current) {
                form.get('is_current').setValue(true, { eventEmit: false });
            }
        }
    }
}
